import React from "react";
import { Link } from "react-router-dom";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-6 px-6 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Servisfy</h1>
            <p className="text-purple-200 text-sm">by ELSE DIGITAL SOLUTIONS INC.</p>
          </div>
          <Link
            to="/login"
            className="text-sm text-purple-200 hover:text-white underline underline-offset-2 transition-colors"
          >
            ← Back to Login
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
        <p className="text-gray-500 text-sm mb-10">
          Last Updated: May 9, 2026 &nbsp;|&nbsp; Effective Date: May 9, 2026
        </p>

        <div className="space-y-10 text-gray-700 leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              1. Acceptance of Terms
            </h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between
              you ("User," "you," or "your") and ELSE DIGITAL SOLUTIONS INC. ("Company," "we,"
              "our," or "us"), the operator of the Servisfy platform, located at{" "}
              <strong>servisfy.com</strong>.
            </p>
            <p className="mt-3">
              By accessing or using the Servisfy Service — including the web application, CRM
              platform, AI-powered customer support tools, and any associated SMS, WhatsApp, or
              voice communication services — you agree to be bound by these Terms. If you do
              not agree to these Terms, you must not use the Service.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              2. Description of Service
            </h2>
            <p>
              Servisfy is an AI-powered customer relationship management (CRM) and omnichannel
              communication platform designed for businesses. The Service enables businesses to:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Manage customer interactions across chat, voice, SMS, and WhatsApp channels.</li>
              <li>Deploy AI-powered agents for automated customer support resolution.</li>
              <li>Create and manage support tickets, escalation workflows, and solution banks.</li>
              <li>
                Communicate with customers using business phone numbers registered through
                Twilio on behalf of the business account holder.
              </li>
              <li>Access analytics, reporting, and operational dashboards for support teams.</li>
            </ul>
            <p className="mt-3">
              Features and availability may vary by subscription tier. We reserve the right to
              modify, suspend, or discontinue any part of the Service at any time with
              reasonable notice.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              3. SMS and WhatsApp Messaging Terms
            </h2>
            <p className="mb-4">
              By providing a phone number and opting in to receive messages from a business
              operating on the Servisfy platform, you agree to the following messaging terms:
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">3.1 Program Description</h3>
                <p>
                  Servisfy enables businesses to send and receive automated and agent-assisted
                  messages via SMS and WhatsApp for the purposes of customer support, service
                  notifications, appointment confirmations, billing alerts, and general business
                  communications.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">3.2 Consent and Opt-In</h3>
                <p>
                  You consent to receive text messages by providing your phone number to a
                  business and initiating contact or explicitly opting in. Consent to receive
                  automated messages is not a condition of any purchase or service enrollment.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">3.3 Message Frequency</h3>
                <p>
                  Message frequency varies. You may receive multiple messages per support
                  interaction or per notification event, depending on the nature of your request
                  and the business's configuration.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">
                  3.4 Message and Data Rates
                </h3>
                <p>
                  Message and data rates may apply. These charges are assessed by your mobile
                  carrier and are not collected or controlled by Servisfy or ELSE DIGITAL
                  SOLUTIONS INC.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">
                  3.5 How to Stop Messages (STOP)
                </h3>
                <p className="text-purple-800">
                  To opt out of receiving SMS or WhatsApp messages from a Servisfy-powered
                  sender, reply <strong>STOP</strong> to any message at any time. Upon receipt
                  of your STOP request, you will receive a one-time confirmation message, and no
                  further messages will be sent to your number from that sender. To re-subscribe,
                  contact the business directly or text <strong>START</strong> to the same number.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  3.6 How to Get Help (HELP)
                </h3>
                <p>
                  To receive help regarding our messaging service, reply <strong>HELP</strong>{" "}
                  to any message. You will receive information about the service and how to
                  contact support. You may also reach our support team at:{" "}
                  <a
                    href="mailto:support@servisfy.com"
                    className="text-purple-700 hover:underline"
                  >
                    support@servisfy.com
                  </a>
                  .
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">
                  3.7 No Third-Party Marketing Sharing
                </h3>
                <p>
                  No mobile opt-in data, phone numbers, or messaging consent information will
                  be shared with third parties or affiliates for marketing or promotional
                  purposes. All information shared with subcontractors is limited to what is
                  strictly necessary to operate the messaging service.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">3.8 Carrier Disclaimer</h3>
                <p>
                  Carriers are not liable for delayed or undelivered messages. Servisfy is not
                  responsible for delays caused by carrier routing, network congestion, or
                  device availability.
                </p>
              </div>
            </div>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              4. Account Registration and Responsibilities
            </h2>
            <p className="mb-2">
              Business accounts must be registered by an authorized representative of a lawful
              business entity. By registering an account, you represent and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You have the legal authority to bind the business to these Terms.</li>
              <li>
                All information you provide during registration is accurate, current, and
                complete.
              </li>
              <li>
                You will maintain the accuracy of your account information and update it as
                necessary.
              </li>
              <li>
                You are solely responsible for maintaining the confidentiality of your account
                credentials and for all activities that occur under your account.
              </li>
              <li>
                You will use the Service in compliance with all applicable laws, including
                telecommunications regulations (TCPA, CTIA guidelines), anti-spam laws (CAN-SPAM
                Act), and applicable carrier guidelines.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              5. Prohibited Uses
            </h2>
            <p className="mb-2">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Send unsolicited messages (spam) or messages to individuals who have not provided
                consent.
              </li>
              <li>
                Send messages containing prohibited content including hate speech, harassment,
                adult content, illegal goods/services, or malicious links.
              </li>
              <li>
                Impersonate any person or entity, or falsely represent your identity or
                affiliation.
              </li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>
                Attempt to gain unauthorized access to the Service or its related systems.
              </li>
              <li>Use the Service for phishing, fraud, or any unlawful purpose.</li>
              <li>
                Resell or sublicense access to the Service without prior written authorization
                from ELSE DIGITAL SOLUTIONS INC.
              </li>
            </ul>
            <p className="mt-3">
              Violations of these prohibited uses may result in immediate suspension or
              termination of your account and may be reported to regulatory authorities.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              6. Subscription and Billing
            </h2>
            <p>
              Access to Servisfy's features is subject to the subscription plan selected at
              registration. Billing is processed through Stripe and governed by the pricing
              terms presented at the time of purchase. Subscription fees are charged on a
              recurring basis (monthly or annual, as selected) and are non-refundable except
              as required by applicable law or as expressly stated in our refund policy.
            </p>
            <p className="mt-3">
              We reserve the right to modify pricing with at least 30 days' notice. Continued
              use of the Service after a price change constitutes your acceptance of the new
              pricing.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              7. Intellectual Property
            </h2>
            <p>
              All content, software, features, and functionality of the Servisfy platform,
              including but not limited to the AI models, interfaces, trademarks, and service
              marks, are owned by ELSE DIGITAL SOLUTIONS INC. or its licensors and are
              protected by applicable intellectual property laws.
            </p>
            <p className="mt-3">
              You are granted a limited, non-exclusive, non-transferable license to use the
              Service for its intended business purposes during the term of your subscription.
              You may not copy, reproduce, distribute, modify, or create derivative works of
              any part of the Service without our express written consent.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              8. Disclaimer of Warranties
            </h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p className="mt-3">
              We do not warrant that the Service will be uninterrupted, error-free, or free of
              viruses or other harmful components. AI-generated responses are provided for
              informational and support purposes only and should not be relied upon as
              professional, legal, or medical advice.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              9. Limitation of Liability
            </h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ELSE DIGITAL SOLUTIONS INC.,
              ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE
              FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
              INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION
              WITH YOUR USE OF, OR INABILITY TO USE, THE SERVICE.
            </p>
            <p className="mt-3">
              Our total liability to you for any claim arising from or relating to these Terms
              or the Service shall not exceed the amount paid by you to us in the 12 months
              preceding the claim.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              10. Termination
            </h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the
              Service at any time, with or without cause, and with or without notice, if we
              reasonably believe you have violated these Terms or applicable law. Upon
              termination, your right to use the Service will cease immediately.
            </p>
            <p className="mt-3">
              You may cancel your account at any time by contacting us at{" "}
              <a
                href="mailto:support@servisfy.com"
                className="text-purple-700 hover:underline"
              >
                support@servisfy.com
              </a>
              . Cancellation does not entitle you to a refund of prepaid subscription fees.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              11. Governing Law and Dispute Resolution
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              applicable jurisdiction in which ELSE DIGITAL SOLUTIONS INC. is incorporated,
              without regard to its conflict of law provisions.
            </p>
            <p className="mt-3">
              Any dispute arising from or relating to these Terms or the Service shall first be
              subject to good-faith negotiation. If unresolved, disputes shall be submitted to
              binding arbitration in accordance with applicable arbitration rules. You waive any
              right to participate in class-action proceedings.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              12. Changes to These Terms
            </h2>
            <p>
              We reserve the right to update or modify these Terms at any time. We will provide
              notice of material changes by updating the "Last Updated" date at the top of this
              page or by notifying you via email or through the Service. Your continued use of
              the Service following such changes constitutes your acceptance of the revised
              Terms.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Contact Us</h2>
            <p>
              For questions about these Terms and Conditions or the Servisfy Service, please
              contact us:
            </p>
            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-1 text-sm">
              <p className="font-semibold text-gray-900">ELSE DIGITAL SOLUTIONS INC.</p>
              <p>Operating as: Servisfy</p>
              <p>
                Email:{" "}
                <a
                  href="mailto:support@servisfy.com"
                  className="text-purple-700 hover:underline"
                >
                  support@servisfy.com
                </a>
              </p>
              <p>Website: servisfy.com</p>
            </div>
          </section>

          {/* Footer links */}
          <div className="pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between text-sm text-gray-500">
            <span>© 2026 ELSE DIGITAL SOLUTIONS INC. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/privacy-policy" className="text-purple-700 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="text-purple-700 hover:underline">
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
